import { useEffect, useRef, useState } from "react";
import _ from "lodash/fp";
import { Controller, useFormContext } from "react-hook-form";
import { Typography } from "@mui/material";

// Needed for testing (https://github.com/facebook/jest/issues/936#issuecomment-214939935)
import * as self from "./index";

import {
  transformContentForEditor,
  transformContentFromEditor,
} from "../../services/contentUtils";
import { Editor } from "../Editor";
import { useReadyContext } from "../../providers/ReadyProvider";
import {
  hasContentEqualsInitialImage,
  sanitizeMathMLContent,
} from "./ControlledFormEditorUtils";

const ControlledFormEditor = ({
  _actions = [],
  assets = {},
  comments: commentsPath,
  contentArea,
  control,
  defaultValue,
  functions = {},
  id,
  interactions,
  label,
  name,
  onChange: handleChange = () => {},
  onReady: handleReady = () => {},
  options = [],
  readonly,
  suggestions: suggestionsPath,
  trigger,
  type,
  rules = {},
  ...props
}) => {
  const [editor, setEditor] = useState();
  const lastProcessedContentRef = useRef(null);
  const { getValues, setValue, reset, formState } = useFormContext();

  const {
    markReady,
    areComponentsReady,
    getInitialEditorData,
    insertInitialEditorData,
  } = useReadyContext();

  useEffect(() => {
    if (formState.isSubmitted) {
      const data = getValues();
      let initialData = { item: null, passage: null };

      if (data.item && data.item.content) {
        initialData.item = sanitizeMathMLContent(data.item.content);
      }

      if (data.passage && data.passage.content) {
        initialData.passage = sanitizeMathMLContent(data.passage.content);
      }

      const initialDataInProvider = getInitialEditorData();

      if (initialData !== initialDataInProvider) {
        insertInitialEditorData(initialData);
      }
    }
  }, [formState.isSubmitted, getValues]);

  const handleChanges = (onChange) =>
    _.debounce(300, (_event, editor) => {
      if (editor.state === "destroyed" || getValues(name) === undefined) {
        return;
      }

      if (
        !options.includes("content") &&
        !options.includes("referenceContent")
      ) {
        handleChange(editor);
        const content = editor.data.get();
        if (content !== lastProcessedContentRef.current) {
          lastProcessedContentRef.current = content;
          onChange(content);
        }
      } else {
        const formData = getValues();
        const content = editor.data.get();
        // Check to see if we should prevent the change being made to the content
        if (options.includes("content")) {
          const contentChangePrevented =
            preventContentChanges(editor)(options)(formData)(content);
          if (contentChangePrevented) return;
        }
        handleContentSuggestions(editor);
        handleChange(editor);
        handleOnChangeTransformations(content)(formData)(onChange);

        markReady();
      }
      trigger && trigger();
    });

  const handleOnChangeTransformations =
    (content) => (formData) => (onChange) => {
      if (areComponentsReady()) {
        if (content === lastProcessedContentRef.current) {
          return;
        }
        lastProcessedContentRef.current = content;

        const transformedContent = _.pipe([
          transformContentFromEditor(name)(formData),
        ])(content);

        onChange(transformedContent);

        const initialEditorData = getInitialEditorData();

        if (
          !hasContentEqualsInitialImage(initialEditorData.item)(
            transformedContent,
          ) &&
          !hasContentEqualsInitialImage(initialEditorData.passage)(
            transformedContent,
          )
        ) {
          return;
        }

        if (
          initialEditorData.item ===
            sanitizeMathMLContent(transformedContent) ||
          initialEditorData.passage ===
            sanitizeMathMLContent(transformedContent)
        ) {
          reset(undefined, {
            keepValues: true,
            keepDirty: false,
            keepDefaultValues: false,
          });
        }
      } else {
        _.pipe([transformContentFromEditor(name)(formData)])(content);
      }
    };

  const handleContentSuggestions = (editor) => {
    const commentsRepository = editor.plugins.get("CommentsRepository");
    commentsRepository?.isEnabled && saveComments(commentsRepository);
    const trackChanges = editor.plugins.get("TrackChanges");
    trackChanges?.isEnabled && saveSuggestions(trackChanges);
  };

  const onReady = (ed) => {
    setEditor(ed);
    handleReady(ed);

    // Set the initial data from the form
    _.pipe([
      // If this is a content editor, transform the initial data
      (content) =>
        options.includes("content")
          ? transformContentForEditor(name)(getValues())(content)
          : content,
      (content) => {
        ed.data.set(content);

        lastProcessedContentRef.current = ed.data.get();

        handleOnReadyInitialData(content);
      },
    ])(getValues(name) ?? defaultValue ?? "<p></p>");
  };

  const handleOnReadyInitialData = (content) => {
    const currentItemValue = sanitizeMathMLContent(getValues("item.content"));
    const currentPassageValue = sanitizeMathMLContent(
      getValues("passage.content"),
    );
    const handledContent = sanitizeMathMLContent(content);
    const initialEditorData = getInitialEditorData();

    if (
      currentItemValue === handledContent &&
      initialEditorData.item === null
    ) {
      initialEditorData.item = handledContent;
      insertInitialEditorData(initialEditorData);
    }

    if (
      currentPassageValue === handledContent &&
      initialEditorData.passage === null
    ) {
      initialEditorData.passage = handledContent;
      insertInitialEditorData(initialEditorData);
    }
  };

  const saveComments = (plugin) => {
    if (!commentsPath) return;
    const value = plugin.getCommentThreads({
      ignoreResolvedComments: true,
      skipEmpty: true,
      skipNotAttached: true,
      toJSON: true,
    });
    if (JSON.stringify(getValues(commentsPath) ?? []) === JSON.stringify(value))
      return;
    setValue(commentsPath, value, { shouldDirty: true });
  };

  const saveSuggestions = (plugin) => {
    if (!suggestionsPath) return;
    const value = plugin.getSuggestions({
      skipNotAttached: true,
      toJSON: true,
    });
    if (
      JSON.stringify(getValues(suggestionsPath) ?? []) === JSON.stringify(value)
    )
      return;
    setValue(suggestionsPath, value, { shouldDirty: true });
  };

  useEffect(() => {
    if (!editor?.plugins.has("CommentsRepository")) return;

    const commentsRepository = editor.plugins.get("CommentsRepository");
    commentsRepository.on("addComment", (_eventInfo) => {
      saveComments(commentsRepository);
    });
    commentsRepository.on("addCommentThread", (_eventInfo) => {
      saveComments(commentsRepository);
    });
    commentsRepository.on("removeComment", (_eventInfo) => {
      saveComments(commentsRepository);
    });
    commentsRepository.on("removeCommentThread", (_eventInfo) => {
      saveComments(commentsRepository);
    });
    commentsRepository.on("reopenCommentThread", (_eventInfo) => {
      saveComments(commentsRepository);
    });
    commentsRepository.on("resolveCommentThread", (_eventInfo) => {
      saveComments(commentsRepository);
    });
    commentsRepository.on("updateComment", (_eventInfo) => {
      saveComments(commentsRepository);
    });
    commentsRepository.on("updateCommentThread", (_eventInfo) => {
      saveComments(commentsRepository);
    });
    return () => {
      commentsRepository.off("addComment");
      commentsRepository.off("addCommentThread");
      commentsRepository.off("reopenCommentThread");
      commentsRepository.off("resolveCommentThread");
      commentsRepository.off("removeComment");
      commentsRepository.off("removeCommentThread");
      commentsRepository.off("updateComment");
      commentsRepository.off("updateCommentThread");
    };
  }, [editor]);

  return (
    <>
      {label && (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="subtitle2">{label}</Typography>
          {rules && rules.required && (
            <Typography variant="subtitle2">Required</Typography>
          )}
        </div>
      )}
      <Controller
        control={control}
        defaultValue={defaultValue}
        name={name}
        rules={{ required: rules.required }}
        render={({ field: { onChange, onBlur } }) => (
          <Editor
            _actions={_actions}
            assets={assets}
            commentsPath={commentsPath}
            contentArea={contentArea}
            functions={{ ...functions, getValues }}
            id={id}
            interactions={interactions}
            name={name}
            onBlur={(e) => {
              onBlur(e);
              trigger && trigger();
            }}
            onChange={handleChanges(onChange)}
            onReady={onReady}
            options={options}
            readonly={readonly}
            suggestionsPath={suggestionsPath}
            type={type}
            {...props}
          />
        )}
      />
    </>
  );
};

const preventContentChanges =
  (editor) =>
  (options = []) =>
  (formValues = { item: {}, passage: {} }) =>
  (content) => {
    return (
      (options.includes("createHighlight") &&
        self.preventMarkDelete(editor)({
          highlights: [],
          ...formValues.passage,
        })(content)) ||
      (options.includes("createInteraction") &&
        self.preventInteractionDelete(editor)({
          interactions: [],
          ...formValues.item,
        })(content))
    );
  };

const preventInteractionDelete = (editor) => (item) => (content) => {
  // If the user tries to delete interactions from the editor, reset the content
  const contentElement = window.document.createElement("div");
  contentElement.innerHTML = content;

  const inContent = item.interactions
    .filter((o) => !!o.identifier)
    .map((interaction) => {
      let interactionElements;
      switch (interaction.__typename) {
        case "ChoiceInteraction":
        case "ExtendedTextInteraction":
        case "GraphicGapMatchInteraction":
        case "MatchInteraction":
        case "MatchTableInteraction":
        case "OrderInteraction":
        case "HotspotInteraction":
          interactionElements = contentElement.querySelectorAll(
            `section.interaction[data-interaction-name="${interaction.identifier}"`,
          );
          return !!interactionElements && interactionElements.length === 1;
        case "GapMatchInteraction":
          const selector = interaction.targets.reduce((acc, { identifier }) => {
            return (
              acc +
              `,slot.interaction[data-interaction-name="${interaction.identifier}"][data-interaction-element="${identifier}"]`
            );
          }, `section.interaction[data-interaction-name="${interaction.identifier}"][data-interaction-element="Elements"]`);
          interactionElements = contentElement.querySelectorAll(selector);
          return (
            !!interactionElements &&
            interactionElements.length === 1 + interaction.targets.length
          );
        case "InlineChoiceInteraction":
        case "DemographicTextEntryInteraction":
        case "TextEntryInteraction":
          interactionElements = contentElement.querySelectorAll(
            `slot.interaction[data-interaction-name="${interaction.identifier}"`,
          );
          return !!interactionElements && interactionElements.length === 1;
        default:
          console.warn(
            "Unidentified interaction of type",
            interaction.__typename,
          );
          return false;
      }
    });

  if (inContent.includes(false)) {
    editor.data.set(item.content);
    return true;
  }
};

const preventMarkDelete = (editor) => (passage) => (content) => {
  // If the user tries to delete highlight tags from the editor, reset the content
  const contentElement = window.document.createElement("div");
  contentElement.innerHTML = content;

  const allContent = [...passage.highlights, ...passage.dynamicTags];

  const inContent = allContent.map((tag) => {
    switch (tag?.__typename) {
      case "Highlight":
      case "DynamicModifyContentTag":
        const beginMarkExists = !!contentElement.querySelector(
          `mark[data-uuid="${tag.begin}"]`,
        );
        const endMarkExists = !!contentElement.querySelector(
          `mark[data-uuid="${tag.end}"]`,
        );
        return beginMarkExists && endMarkExists;

      case "ItemPointReference":
      case "DynamicBreakParagraphTag":
        const pointExists = !!contentElement.querySelector(
          `mark[data-uuid="${tag.point}"]`,
        );
        return pointExists;

      case "DynamicRemoveLabelsTag":
        tag.labels?.map((label) => {
          const beginMarkExists = !!contentElement.querySelector(
            `mark[data-uuid="${label.begin}"]`,
          );
          const endMarkExists = !!contentElement.querySelector(
            `mark[data-uuid="${label.end}"]`,
          );
          return beginMarkExists && endMarkExists;
        });

      case "DynamicOrderContentTag":
        const orderExits = tag.order?.map((o) => {
          return !!contentElement.querySelector(`mark[data-uuid="${o}"]`);
        });
        return orderExits;
    }
  });

  if (inContent.includes(false)) {
    editor.data.set(passage.content);
    return true;
  }
};

export {
  ControlledFormEditor,
  preventContentChanges,
  preventInteractionDelete,
  preventMarkDelete,
};
